import "./_diagram-styles.scss";

import { initializeDiagramLines } from './diagram-lines';

const ITEM_STAGGER_DELAY = 50;
const MOBILE_COLUMN_DELAY = 120;
const MOBILE_COLUMN_ANIMATION_DURATION = 630;
const MOBILE_ITEMS_START_DELAY = MOBILE_COLUMN_DELAY + MOBILE_COLUMN_ANIMATION_DURATION;
const MOBILE_ITEM_STAGGER_DELAY = 130;
const ITEM_ANIMATION_DURATION = 240;
const LINE_STAGGER_DELAY = 105;
const LINE_ANIMATION_DURATION = 180;
const LINE_CSS_ANIMATION_DURATION = 630;
const PHASE_GAP_DELAY = 0;
const LINE_TO_ITEM_OVERLAP = 160;
const DESKTOP_DIAGRAM_MEDIA_QUERY = '(min-width: 768px)';
const DIAGRAM_LINES_DRAWN_ATTRIBUTE = 'data-diagram-lines-drawn';
const diagramLineCleanupMap = new WeakMap();
const DESKTOP_STATIC_CLASS = 'is-desktop-static';

/**
 * Determine whether the animated SVG diagram should run for the current viewport.
 *
 * @return {boolean} True when desktop enhancements should be enabled.
 */
const shouldEnhanceDiagram = () => window.matchMedia( DESKTOP_DIAGRAM_MEDIA_QUERY ).matches;

/**
 * Convert a CSS time value into milliseconds.
 *
 * @param {string} value CSS time value.
 * @return {number} Duration in milliseconds.
 */
const parseCssTimeToMs = ( value ) => {

	if ( ! value ) {

		return 0;

	}

	if ( value.endsWith( 'ms' ) ) {

		return Number.parseFloat( value ) || 0;

	}

	if ( value.endsWith( 's' ) ) {

		return ( Number.parseFloat( value ) || 0 ) * 1000;

	}

	return Number.parseFloat( value ) || 0;

};

/**
 * Mark SVG lines as fully drawn after the initial entrance animation completes.
 *
 * @param {HTMLElement} diagramElement Diagram block root element.
 * @return {void}
 */
const scheduleDiagramLinesAsDrawn = ( diagramElement ) => {

	const pathElements = Array.from( diagramElement.querySelectorAll( '.b-diagram__lines path' ) );
	const maxLineDelay = pathElements.reduce( ( maxDelay, pathElement ) => {

		const currentDelay = parseCssTimeToMs(
			pathElement.style.getPropertyValue( '--diagram-line-delay' )
		);

		return Math.max( maxDelay, currentDelay );

	}, 0 );
	const lineAnimationDuration = parseCssTimeToMs(
		window.getComputedStyle( diagramElement ).getPropertyValue( '--diagram-line-animation-duration' )
	);

	window.setTimeout( () => {

		diagramElement.setAttribute( DIAGRAM_LINES_DRAWN_ATTRIBUTE, 'true' );

	}, maxLineDelay + lineAnimationDuration );

};

/**
 * Read stored diagram connections from the canvas element.
 *
 * @param {HTMLElement|null} canvasElement Diagram canvas element.
 * @return {Array} Parsed connection list.
 */
const getConnections = ( canvasElement ) => {

	try {

		return JSON.parse( canvasElement?.dataset?.diagramConnections || '[]' );

	} catch ( error ) {

		return [];

	}

};

/**
 * Assign staged animation timing to diagram items and connection groups.
 *
 * @param {HTMLElement} diagramElement Diagram block root element.
 * @return {void}
 */
const prepareDiagramAnimations = ( diagramElement ) => {

	const columns = Array.from( diagramElement.querySelectorAll( '.b-diagram__column' ) );
	const canvasElement = diagramElement.querySelector( '.b-diagram__canvas' );
	const connections = getConnections( canvasElement );
	const itemColumnMap = new Map();
	const incomingConnectionsCount = new Map();
	let currentDelay = 0;

	columns.forEach( ( column, columnIndex ) => {

		column.dataset.diagramColumnIndex = `${ columnIndex }`;
		column.dataset.diagramIncomingLineDelay = '0';

		column.querySelectorAll( '.b-diagram__item' ).forEach( ( itemElement ) => {

			const itemId = itemElement.dataset.itemId;

			if ( itemId ) {

				itemColumnMap.set( itemId, columnIndex );

			}

		} );

	} );

	connections.forEach( ( connection ) => {

		const targetColumnIndex = itemColumnMap.get( connection.to );
		const sourceColumnIndex = itemColumnMap.get( connection.from );

		if (
			Number.isInteger( targetColumnIndex ) &&
			Number.isInteger( sourceColumnIndex ) &&
			targetColumnIndex > sourceColumnIndex
		) {

			incomingConnectionsCount.set(
				targetColumnIndex,
				( incomingConnectionsCount.get( targetColumnIndex ) || 0 ) + 1
			);

		}

	} );

	columns.forEach( ( column, columnIndex ) => {

		const itemElements = Array.from( column.querySelectorAll( '.b-diagram__item' ) );

		itemElements.forEach( ( itemElement, itemIndex ) => {

			itemElement.style.setProperty(
				'--diagram-item-delay',
				`${ currentDelay + ( itemIndex * ITEM_STAGGER_DELAY ) }ms`
			);
			itemElement.style.setProperty( '--diagram-item-order', `${ itemIndex }` );
			itemElement.dataset.diagramColumnIndex = `${ columnIndex }`;

		} );

		if ( itemElements.length > 0 ) {

			currentDelay += ( ( itemElements.length - 1 ) * ITEM_STAGGER_DELAY ) + ITEM_ANIMATION_DURATION + PHASE_GAP_DELAY;

		}

		if ( columnIndex >= columns.length - 1 ) {

			return;

		}

		const nextColumnElement = columns[ columnIndex + 1 ];
		const nextColumnConnectionsCount = incomingConnectionsCount.get( columnIndex + 1 ) || 0;

		nextColumnElement.dataset.diagramIncomingLineDelay = `${ currentDelay }`;

		if ( nextColumnConnectionsCount > 0 ) {

			currentDelay +=
				( ( nextColumnConnectionsCount - 1 ) * LINE_STAGGER_DELAY ) +
				Math.max( LINE_ANIMATION_DURATION - LINE_TO_ITEM_OVERLAP, 0 ) +
				PHASE_GAP_DELAY;

		}

	} );

	diagramElement.classList.add( 'has-entrance-animation' );
	diagramElement.dataset.diagramLineStaggerDelay = `${ LINE_STAGGER_DELAY }`;
	diagramElement.style.setProperty( '--diagram-line-animation-duration', `${ LINE_CSS_ANIMATION_DURATION }ms` );

};

/**
 * Assign per-column staged animation timing for the mobile experience.
 *
 * @param {HTMLElement} diagramElement Diagram block root element.
 * @return {void}
 */
const prepareMobileDiagramAnimations = ( diagramElement ) => {

	const columns = Array.from( diagramElement.querySelectorAll( '.b-diagram__column' ) );

	columns.forEach( ( columnElement ) => {

		const itemElements = Array.from( columnElement.querySelectorAll( '.b-diagram__item' ) );

		itemElements.forEach( ( itemElement, itemIndex ) => {

			itemElement.style.setProperty(
				'--diagram-mobile-item-delay',
				`${ MOBILE_ITEMS_START_DELAY + ( itemIndex * MOBILE_ITEM_STAGGER_DELAY ) }ms`
			);

		} );

		columnElement.style.setProperty(
			'--diagram-mobile-column-delay',
			`${ MOBILE_COLUMN_DELAY }ms`
		);
		columnElement.style.setProperty(
			'--diagram-mobile-column-duration',
			`${ MOBILE_COLUMN_ANIMATION_DURATION }ms`
		);

	} );

	diagramElement.classList.add( 'has-mobile-entrance-animation' );

};

/**
 * Start per-column viewport observers for the mobile entrance animation.
 *
 * @param {HTMLElement} diagramElement Diagram block root element.
 * @return {void}
 */
const initializeMobileDiagramViewportObserver = ( diagramElement ) => {

	const columnElements = Array.from( diagramElement.querySelectorAll( '.b-diagram__column' ) );

	if ( typeof IntersectionObserver === 'undefined' ) {

		columnElements.forEach( ( columnElement ) => {

			columnElement.classList.add( 'is-inview' );

		} );

		return;

	}

	const observer = new IntersectionObserver(
		( entries ) => {

			entries.forEach( ( entry ) => {

				if ( ! entry.isIntersecting ) {

					return;

				}

				entry.target.classList.add( 'is-inview' );
				observer.unobserve( entry.target );

			} );

		},
		{
			threshold: 0.35,
			rootMargin: '0px 0px -8% 0px',
		}
	);

	columnElements.forEach( ( columnElement ) => {

		observer.observe( columnElement );

	} );

};

/**
 * Start or restart desktop line observers for a single diagram.
 *
 * @param {HTMLElement} diagramElement Diagram block root element.
 * @return {void}
 */
const initializeDesktopDiagramLines = ( diagramElement ) => {

	diagramLineCleanupMap.get( diagramElement )?.();

	const cleanup = initializeDiagramLines( diagramElement.querySelector( '.b-diagram__canvas' ) );

	if ( cleanup ) {

		diagramLineCleanupMap.set( diagramElement, cleanup );

	}

};

/**
 * Clear mobile-only animation state before switching to desktop mode.
 *
 * @param {HTMLElement} diagramElement Diagram block root element.
 * @return {void}
 */
const resetMobileDiagramState = ( diagramElement ) => {

	diagramElement.classList.remove( 'has-mobile-entrance-animation' );
	diagramElement.querySelectorAll( '.b-diagram__column' ).forEach( ( columnElement ) => {

		columnElement.classList.remove( 'is-inview' );
		columnElement.style.removeProperty( '--diagram-mobile-column-delay' );
		columnElement.style.removeProperty( '--diagram-mobile-column-duration' );

	} );
	diagramElement.querySelectorAll( '.b-diagram__item' ).forEach( ( itemElement ) => {

		itemElement.style.removeProperty( '--diagram-mobile-item-delay' );

	} );

};

/**
 * Apply the correct diagram mode for the current breakpoint.
 *
 * @param {HTMLElement} diagramElement Diagram block root element.
 * @param {boolean} animateDesktopEntrance Whether desktop should animate on entry.
 * @return {void}
 */
const applyDiagramMode = ( diagramElement, animateDesktopEntrance = false ) => {

	if ( shouldEnhanceDiagram() ) {

		resetMobileDiagramState( diagramElement );
		prepareDiagramAnimations( diagramElement );
		initializeDesktopDiagramLines( diagramElement );

		if ( animateDesktopEntrance ) {

			diagramElement.classList.remove( DESKTOP_STATIC_CLASS );
			initializeDiagramViewportObserver( diagramElement );
			return;

		}

		diagramElement.classList.add( DESKTOP_STATIC_CLASS );
		diagramElement.classList.add( 'is-inview' );
		diagramElement.setAttribute( DIAGRAM_LINES_DRAWN_ATTRIBUTE, 'true' );
		return;

	}

	diagramElement.classList.remove( DESKTOP_STATIC_CLASS );
	prepareMobileDiagramAnimations( diagramElement );
	initializeMobileDiagramViewportObserver( diagramElement );

};

/**
 * Start the viewport observer that triggers the entrance animation once.
 *
 * @param {HTMLElement} diagramElement Diagram block root element.
 * @return {void}
 */
const initializeDiagramViewportObserver = ( diagramElement ) => {

	if ( typeof IntersectionObserver === 'undefined' ) {

		diagramElement.classList.add( 'is-inview' );
		scheduleDiagramLinesAsDrawn( diagramElement );
		return;

	}

	const observer = new IntersectionObserver(
		( entries ) => {

			entries.forEach( ( entry ) => {

				if ( ! entry.isIntersecting ) {

					return;

				}

				diagramElement.classList.add( 'is-inview' );
				scheduleDiagramLinesAsDrawn( diagramElement );
				observer.unobserve( entry.target );

			} );

		},
		{
			threshold: 0.5,
			rootMargin: '0px 0px -10% 0px',
		}
	);

	observer.observe( diagramElement );

};

/**
 * Initialize all frontend diagram behaviors.
 *
 * @return {void}
 */
const initializeDiagrams = () => {

	document.querySelectorAll( '.b-diagram' ).forEach( ( diagramElement ) => {

		const desktopMediaQuery = window.matchMedia( DESKTOP_DIAGRAM_MEDIA_QUERY );

		applyDiagramMode( diagramElement, desktopMediaQuery.matches );
		desktopMediaQuery.addEventListener( 'change', () => applyDiagramMode( diagramElement ) );

	} );

};

if ( document.readyState === 'loading' ) {

	document.addEventListener( 'DOMContentLoaded', initializeDiagrams );

} else {

	initializeDiagrams();

}
